import {
  Autocomplete,
  AutocompleteChangeReason,
  FormControl,
  List,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  IsocenterOptionOut,
  useWebApiV1GetIsocenterOptionsQuery,
} from "@providers/hop-ord-server/api";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

interface Props {
  selectedIsocenter: IsocenterOptionOut;
  setSelectedIsocenter: (isocenter: IsocenterOptionOut) => void;
  selectedRtStructId: number;
  templateName: string;
  isLoading: boolean;
}

const CATEGORY_LABEL = {
  DEFAULT: { backendKey: "DEFAULT", label: "Default" },
  MARKER_POINTS: { backendKey: "MARKER_POINTS", label: "Markers" },
  STRUCTURES: { backendKey: "STRUCTURES", label: "Structures (center of)" },
} as const;

export type { IsocenterOptionOut as IsocenterOption };

const IsocenterSelectbox = ({
  selectedIsocenter,
  setSelectedIsocenter,
  selectedRtStructId,
  templateName,
  isLoading,
}: Props) => {
  const orderId = useParams().orderId;
  const { data: isocenterOptions } = useWebApiV1GetIsocenterOptionsQuery(
    {
      templateName: templateName,
      rtStructId: selectedRtStructId,
      orderId: Number(orderId),
    },
    {
      skip: isLoading,
    },
  );

  useEffect(() => {
    if (isocenterOptions && isocenterOptions.length > 0) {
      setSelectedIsocenter(isocenterOptions[0]);
    }
  }, [isocenterOptions, setSelectedIsocenter]);

  return (
    <FormControl sx={{ flexGrow: 1, width: "100%" }}>
      <Autocomplete
        data-testid="isocenter-selectbox"
        isOptionEqualToValue={(option, value) =>
          option.name === value.name && option.type === value.type
        }
        onChange={(
          _,
          newValue: IsocenterOptionOut | null,
          reason: AutocompleteChangeReason,
        ) => {
          if (reason === "clear") {
            setSelectedIsocenter(
              isocenterOptions?.[0] || {
                name: "",
                type: "",
                optionLabel: "",
                category: "",
              },
            );
          } else {
            setSelectedIsocenter(
              newValue ||
                isocenterOptions?.[0] || {
                  name: "",
                  type: "",
                  optionLabel: "",
                  category: "",
                },
            );
          }
        }}
        size="small"
        slotProps={{
          paper: {
            style: {
              maxHeight: 500,
            },
          },
        }}
        options={isocenterOptions || []}
        getOptionLabel={(option) =>
          option.optionLabel ? `${option.optionLabel}` : `${option.name}` || ""
        }
        value={selectedIsocenter}
        fullWidth
        groupBy={(option) => option.category}
        renderGroup={(params) => {
          return (
            <List key={params.key} sx={{ p: 0 }}>
              {params.group === CATEGORY_LABEL.DEFAULT.backendKey ? (
                <></>
              ) : (
                <ListItem>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {
                      CATEGORY_LABEL[
                        params.group as keyof typeof CATEGORY_LABEL
                      ].label
                    }
                  </Typography>
                </ListItem>
              )}
              {params.children}
            </List>
          );
        }}
        renderInput={(params) => <TextField {...params} />}
      />
    </FormControl>
  );
};

export default IsocenterSelectbox;
